import { YAHOO_TARGET_FIELDS, yahooTargetingRow, applyYahooTargeting, formatYahooGeography } from './yahooTargeting';
// Yahoo's launcher accepts inline lines or lineKey references, and either a
// campaign or campaigns envelope. Resolve references without rewriting the
// plan: edits must preserve packages, targeting, and vendor-specific fields.
import { canonicalPlatform } from '../../../utils/accountSettingPlatformList';

const array = (v) => Array.isArray(v) ? v : [];
const number = (v) => v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v);
const date = (v) => v ? String(v).slice(0, 10) : null;
const label = (v) => v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v);
const list = (v) => array(v).map(label).join(' · ') || '—';
export const isYahooResult = (r) => canonicalPlatform(r?.platform || r?.Platform || '') === 'yahoo';
const campaigns = (r) => Array.isArray(r?.campaigns) ? r.campaigns : r?.campaign ? [r.campaign] : [];

export function yahooLines(result) {
  const cs = campaigns(result);
  const top = array(result?.lines);
  const claimed = new Set(cs.flatMap((c) => array(c.lines).filter((l) => typeof l === 'string')));
  return cs.flatMap((campaign, ci) => {
    const entries = array(campaign.lines).map((entry, li) => ({
      line: typeof entry === 'string' ? top.find((l) => l.lineKey === entry) : entry,
      path: typeof entry === 'string' ? `/lines/${top.findIndex((l) => l.lineKey === entry)}`
        : `${Array.isArray(result.campaigns) ? `/campaigns/${ci}` : '/campaign'}/lines/${li}`,
    }));
    if (cs.length === 1) top.forEach((line, li) => {
      if (line.lineKey && !claimed.has(line.lineKey)) entries.push({ line, path: `/lines/${li}` });
    });
    return entries.filter(({ line }) => line && typeof line === 'object')
      .map((entry, li) => ({ ...entry, campaign, ci, li }));
  });
}

// One row per flight, explicitly labelled, so edits to one flight never
// flatten or overwrite a line's other schedules. Line-level edits are shared
// by all flights of that line, just as campaign-level edits are elsewhere.
function flights(result) {
  return yahooLines(result).flatMap((entry) => {
    const schedules = array(entry.line.schedules);
    return (schedules.length ? schedules : [entry.line]).map((schedule, si) => ({
      ...entry, schedule, si, scheduled: schedules.length > 0,
      rowId: `yahoo-${entry.ci}-${entry.li}-${si}`,
    }));
  });
}
const amount = (s, impression) => number(impression ? s.impressions ?? s.budget : s.budget);
export function buildYahooReviewRows(result) {
  const rows = flights(result).map(({ line, campaign, schedule, scheduled, si, path, rowId }, idx) => {
    const impression = line.budgetType === 'IMPRESSION';
    const targeting = line.targeting || {};
    return {
      rowId, yahooLineId: path, __refPath: scheduled ? `${path}/schedules/${si}` : path, idx: idx + 1,
      name: line.name || '—', channel: 'Yahoo DSP', market: campaign.name || '—',
      package: line.packageName || line.packageId || '—',
      fmt: line.mediaType || '—', currencyCode: campaign.currency || null,
      flight: scheduled ? schedule.scheduleName || `Flight ${si + 1}` : 'Single flight',
      start: date(schedule.startDate ?? schedule.startDateStr ?? line.startDate ?? campaign.startDate),
      end: date(schedule.endDate ?? schedule.endDateStr ?? line.endDate ?? campaign.endDate),
      // Separate units: an impression budget must never be shown as dollars.
      budget: impression ? null : amount(schedule, false),
      imps: impression ? amount(schedule, true) : null,
      dailyBudget: impression ? null : number(schedule.dailyBudget),
      budgetType: line.budgetType || 'CURRENCY',
      bidFloor: number(line.bidPrice), goalType: line.goalType || '—',
      goalAmount: number(line.goalAmount), pacing: line.pacingModeType || '—',
      entityStatus: line.status || 'PAUSED',
      devices: list(targeting.technology?.deviceTypes),
      targeting: label(targeting), ...yahooTargetingRow(result, line), annotations: {},
    };
  });
  const issues = yahooIssues(result);
  if (rows.length) {
    if (issues.errors.length) rows[0].annotations.name = { type: 'm', msg: issues.errors.join('\n') };
    if (issues.warnings.length) rows[0].annotations.targeting = { type: 'q', msg: issues.warnings.join('\n') };
  }
  return rows;
}

function yahooIssues(result) {
  const errors = array(result.templateErrors).map(label);
  const topKeys = new Set(array(result.lines).map((l) => l.lineKey));
  for (const c of campaigns(result)) {
    for (const ref of array(c.lines)) {
      if (typeof ref === 'string' && !topKeys.has(ref)) errors.push(`Campaign ${c.name || ''} references missing Yahoo line ${ref}.`);
    }
  }
  return { errors, warnings: [...array(result.templateWarnings), ...array(result.customData?.postProcessing?.warnings)].map(label) };
}

// Count and render the original findings individually. Campaign-wide warnings
// have no row target; never pin them to an unrelated first line.
export function buildYahooFindings(result, rows = []) {
  const issues = yahooIssues(result);
  const normalize = (v) => String(v || '').replace(/\s+/g, ' ').trim();
  return [['m', issues.errors], ['q', issues.warnings]].flatMap(([type, messages]) => messages.map((msg, index) => {
    const text = normalize(msg);
    const startsWithName = (name) => name && text.startsWith(`${normalize(name)}:`);
    const definition = [...array(result.audiences), ...array(result.siteLists)].find((s) => startsWithName(s.name));
    const keys = array(definition?.line_keys ?? definition?.lineKeys);
    const lineNames = yahooLines(result).filter(({ line }) => keys.includes(line.lineKey) || keys.includes(line.name)).map(({ line }) => line.name);
    const matches = rows.filter((r) => startsWithName(r.name) || lineNames.includes(r.name));
    const uniqueLines = new Set(matches.map((r) => r.yahooLineId));
    const row = uniqueLines.size === 1 ? matches[0] : null;
    return { id: `yahoo-${type}-${index}`, type, msg, field: 'name',
      rowId: row?.rowId, rowName: row?.name, scope: row ? row.name : 'Campaign / shared targeting' };
  }));
}

export function breakdownYahooByCampaign(result) {
  const rows = buildYahooReviewRows(result);
  const lines = yahooLines(result);
  return campaigns(result).map((c, ci) => {
    const owned = rows.filter((r) => r.rowId.startsWith(`yahoo-${ci}-`));
    const schedules = array(c.budgetSchedules);
    const sum = (key) => owned.reduce((n, r) => n + (Number(r[key]) || 0), 0);
    return {
      key: `yahoo-${ci}`, packageName: c.name || '—', categoryLabel: 'Yahoo DSP',
      lineItemCount: lines.filter((l) => l.ci === ci).length,
      impressions: c.budgetType === 'IMPRESSION'
        ? (schedules.length ? schedules.reduce((n, s) => n + (number(s.impressions ?? s.scheduleBudget) || 0), 0) : number(c.budget) ?? sum('imps')) : sum('imps'),
      budget: c.budgetType === 'IMPRESSION' ? null
        : schedules.length ? schedules.reduce((n, s) => n + (number(s.scheduleBudget) || 0), 0) : number(c.budget) ?? sum('budget'),
      flightStart: date(c.startDate) || schedules.map((s) => date(s.startDate ?? s.startDateStr)).filter(Boolean).sort()[0] || owned.map((r) => r.start).filter(Boolean).sort()[0],
      flightEnd: date(c.endDate) || schedules.map((s) => date(s.endDate ?? s.endDateStr)).filter(Boolean).sort().pop() || owned.map((r) => r.end).filter(Boolean).sort().pop(),
      rate: null, sizes: '—',
    };
  });
}
export function summarizeYahooResult(result) {
  const groups = breakdownYahooByCampaign(result);
  const issues = yahooIssues(result);
  return {
    platform: 'Yahoo DSP', ioCount: campaigns(result).length,
    adGroupCount: yahooLines(result).length, packageCount: campaigns(result).reduce((n, c) => n + array(c.packages).length, 0),
    totalBudget: groups.some((g) => g.budget != null) ? groups.reduce((n, g) => n + (g.budget || 0), 0) : null,
    totalImpressions: groups.reduce((n, g) => n + (g.impressions || 0), 0), avgCpm: null,
    campaignName: campaigns(result)[0]?.name, advertiserId: result.advertiserId,
    advertiserName: result.advertiserName,
    flightStart: groups.map((g) => g.flightStart).filter(Boolean).sort()[0],
    flightEnd: groups.map((g) => g.flightEnd).filter(Boolean).sort().pop(),
    issueCount: issues.errors.length + issues.warnings.length,
    errorCount: issues.errors.length, warningCount: issues.warnings.length,
  };
}
export function extractYahooCampaignConfig(result) {
  const c = campaigns(result)[0] || {};
  return { advertiserId: result.advertiserId, goal: c.goalType, kpi: c.goalValue,
    budgetType: c.budgetType || 'CURRENCY',
    freqCap: c.frequencyCapPeriodType === 'UNLIMITED' ? 'Unlimited' : `${c.frequencyCapValue ?? '—'} / ${c.frequencyCapPeriodType || '—'}`,
    geoSummary: [...new Set(buildYahooReviewRows(result).map((r) => r.geo))].join(' · '),
  };
}

const col = (k, l, fmt) => ({ k, l, w: 150, ...(fmt ? { fmt } : {}) });
export const YAHOO_GROUP_COLS = {
  overview: [col('fmt', 'Media type'), col('flight', 'Flight'), col('start', 'Start', 'date'), col('end', 'End', 'date'), col('budget', 'Flight budget', 'cur'), col('imps', 'Impressions'), col('budgetType', 'Budget type')],
  flighting: [col('flight', 'Flight'), col('start', 'Start', 'date'), col('end', 'End', 'date'), col('dailyBudget', 'Daily budget', 'cur'), col('pacing', 'Pacing'), col('entityStatus', 'Status')],
  budget: [col('budget', 'Flight budget', 'cur'), col('imps', 'Impressions'), col('dailyBudget', 'Daily budget', 'cur'), col('budgetType', 'Budget type')],
  targeting: [col('yahooGeo', 'Included geography', formatYahooGeography), col('yahooGeoExcluded', 'Excluded geography', formatYahooGeography), col('devices', 'Devices'), col('yahooAudienceSummary', 'Custom audiences'), ...Object.entries(YAHOO_TARGET_FIELDS).filter(([k]) => !['yahooGeo', 'yahooGeoExcluded'].includes(k)).map(([k, l]) => col(k, l)), col('yahooReceiptType', 'Receipt event / attribute'), col('yahooTargetingState', 'Targeting preparation')],
  bid: [col('bidFloor', 'Bid price', 'cur'), col('goalType', 'Goal'), col('goalAmount', 'Goal amount'), col('pacing', 'Pacing')],
};
export const YAHOO_GROUP_KEYS = Object.keys(YAHOO_GROUP_COLS);
export const YAHOO_EDITABLE = new Set(['name', 'start', 'end', 'budget', 'imps', 'dailyBudget', 'bidFloor', 'goalAmount', ...Object.keys(YAHOO_TARGET_FIELDS)]);
export const YAHOO_FIELD_EDITORS = { start: 'date', end: 'date', budget: 'currency', imps: 'number', dailyBudget: 'currency', bidFloor: 'currency', goalAmount: 'number', ...Object.fromEntries(Object.keys(YAHOO_TARGET_FIELDS).map((k) => [k, 'text'])) };
export const YAHOO_LIST_FIELDS = {};
export const YAHOO_READ_ONLY_REASONS = Object.fromEntries(
  Object.values(YAHOO_GROUP_COLS).flat().filter(({ k }) => !YAHOO_EDITABLE.has(k))
    .map(({ k }) => [k, k === 'entityStatus' ? 'Status is controlled by the launch workflow.' : 'Preserved from the Yahoo launch template.']),
);

export function applyYahooEdits(edited, edits, listOps, reviewRows, rowOps = []) {
  if (rowOps.length || Object.values(listOps || {}).some((v) => Object.values(v || {}).some((ops) => ops?.length))) {
    throw new Error('Yahoo review supports field edits only; row and targeting-list operations are not supported.');
  }
  const targets = new Map(flights(edited).map((f) => [f.rowId, f]));
  const lineValues = new Map();
  for (const [id, fields] of Object.entries(edits || {})) {
    const target = targets.get(id);
    for (const [field, value] of Object.entries(fields)) {
      if (!Object.hasOwn(YAHOO_TARGET_FIELDS, field) || !target) continue;
      const key = `${target.path}:${field}`, serialized = JSON.stringify(value);
      if (lineValues.has(key) && lineValues.get(key) !== serialized) throw new Error(`Conflicting ${field} values for flights of the same Yahoo line.`);
      lineValues.set(key, serialized);
    }
  }
  for (const [id, fields] of Object.entries(edits || {})) {
    const target = targets.get(id);
    if (!target) throw new Error(`Yahoo review row no longer exists: ${id}`);
    const { line, schedule } = target;
    for (const [field, value] of Object.entries(fields)) {
      if (!YAHOO_EDITABLE.has(field)) continue;
      if (Object.hasOwn(YAHOO_TARGET_FIELDS, field)) { applyYahooTargeting(edited, line, field, value); continue; }
      if (field === 'name') { line.name = String(value); continue; }
      if (field === 'start' || field === 'end') {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value))) throw new Error('Yahoo flight dates must use YYYY-MM-DD.');
        const key = field === 'start' ? 'startDate' : 'endDate';
        schedule[Object.prototype.hasOwnProperty.call(schedule, `${key}Str`) ? `${key}Str` : key] = value;
        continue;
      }
      const n = number(value);
      if (field === 'imps' && !Number.isInteger(n)) throw new Error('Yahoo impressions must be a whole number.');
      if (n == null || n < 0) throw new Error(`Yahoo ${field} must be a non-negative number.`);
      if (['budget', 'imps', 'dailyBudget'].includes(field)) {
        const impression = line.budgetType === 'IMPRESSION';
        if ((field === 'imps') !== impression) throw new Error('Edit the budget field matching this Yahoo line’s budget type.');
        const key = field === 'imps' ? (Object.prototype.hasOwnProperty.call(schedule, 'impressions') ? 'impressions' : 'budget') : field;
        schedule[key] = n;
        if (field === 'dailyBudget') schedule.dailyBudgetType = 'SPECIFIED_AMOUNT';
      } else line[field === 'bidFloor' ? 'bidPrice' : field] = n;
    }
  }
  return edited;
}
